import { useAction, useAtom } from "@reatom/npm-react";
import { useCallback, useEffect, useMemo } from "react";
import { ActionMeta, OnChangeValue, PropsValue } from "react-select";
import { productSelectionStore } from "src/store/atoms";
import { toastHandler } from "src/util";

import { filterAtom, updateFilters } from "../../../store/atoms/filterQuery";
import {
  getProductSampleRequest,
  getProductSamplesRequest,
  createProductSampleRequest,
} from "../store/fetchers";
import {
  productSamplesOptionsAtom,
  ProductSampleOption,
} from "../store/fetchers/getProductSamplesRequest";

export const useProductSample = () => {
  const getProductSamples = useAction(getProductSamplesRequest);
  const getProductSample = useAction(getProductSampleRequest);
  const createProductSample = useAction(createProductSampleRequest);
  const setCurrentSelection = useAction(
    productSelectionStore.setCurrentSelectionAction,
  );
  const clearSelection = useAction(productSelectionStore.clearSelectionAction);

  const [productSamplesOptions] = useAtom(productSamplesOptionsAtom);
  const [selectedSpuIds] = useAtom(productSelectionStore.selectedArrayAtom);
  const [selectedProductSample] = useAtom(
    productSelectionStore.currentSelectionAtom,
  );
  const [filter] = useAtom(filterAtom);
  const applyFilters = useAction(updateFilters);

  useEffect(() => {
    getProductSamples();
  }, [getProductSamples]);

  const currentValue = useMemo<PropsValue<ProductSampleOption>>(
    () =>
      selectedProductSample
        ? {
            label: selectedProductSample.name,
            value: selectedProductSample.id,
          }
        : null,
    [selectedProductSample],
  );

  const handleSelectOption = useCallback(
    async (option: ProductSampleOption) => {
      const selectedSample = await getProductSample(option.value);
      setCurrentSelection(selectedSample);
    },
    [getProductSample, setCurrentSelection],
  );

  const handleCreateOption = useCallback(
    async (option: ProductSampleOption) => {
      const newSample = await createProductSample({
        name: option.label,
        productIds: selectedSpuIds,
      });

      toastHandler.success({
        name: "saveProductSample",
        title: "Добавление выборки",
        content: "Добавлена новая выборка",
      });

      await getProductSamples();
      setCurrentSelection(newSample);
    },
    [
      createProductSample,
      selectedSpuIds,
      getProductSamples,
      setCurrentSelection,
    ],
  );

  const handleClearOption = useCallback(() => {
    clearSelection();

    if (filter.viewAllSelected) {
      applyFilters({ viewAllSelected: false });
    }
  }, [clearSelection, filter.viewAllSelected, applyFilters]);

  const onSelectChange = useCallback(
    async (
      newValue: OnChangeValue<ProductSampleOption, false>,
      actionMeta: ActionMeta<ProductSampleOption>,
    ) => {
      switch (actionMeta.action) {
        case "select-option":
          if (!newValue) break;
          await handleSelectOption(newValue);
          break;
        case "create-option":
          if (!newValue) break;
          await handleCreateOption(newValue);
          break;
        case "clear":
          handleClearOption();
          break;
        default:
          break;
      }
    },
    [handleSelectOption, handleCreateOption, handleClearOption],
  );

  return {
    value: currentValue,
    options: productSamplesOptions,
    onSelectChange,
  };
};
